import React, { useEffect, useMemo, useState } from 'react';
import { IconCake, IconAward, IconSpeakerphone, IconX } from '@tabler/icons-react';
import { useApp } from '../context/AppContext';
import { todayDateStringInTz, DAY_ANCHOR_TZ } from '../utils/time';
import { parseBirthday } from '../utils/birthdays';
import { parseHireDate } from '../utils/workAnniversaries';
import { daysUntilAnnualDate, yearOfNextOccurrence, formatAnnualDate } from '../utils/annualDates';
import { activeAnnouncements } from '../utils/announcements';

const ROTATE_MS = 4500;

// Every screen mounts its own <Layout>, so this component remounts on each
// route change — a plain useState would forget the dismissal the moment
// someone clicks to a different page. This module-level flag survives
// route changes but still resets on an actual page reload / sign-in.
let sessionDismissed = false;

// Both event types share the same visibility rule: the 7-day lead-up is a
// shoutout to everyone ELSE (seeing your own countdown would be a little
// odd), but on the actual day the person themselves gets their own greeting
// too, not just everyone around them.
export default function AnnouncementBanner() {
  const { currentUser, users, announcements } = useApp();
  const [dismissed, setDismissed] = useState(sessionDismissed);
  const [index, setIndex] = useState(0);

  const upcoming = useMemo(() => {
    if (!currentUser) return [];
    const today = todayDateStringInTz(DAY_ANCHOR_TZ);
    const items = [];

    // Admin-authored announcements — no self/others distinction, everyone
    // sees the same thing, unlike birthdays/anniversaries below.
    activeAnnouncements(announcements, today).forEach((a) => {
      items.push({ type: 'custom', message: a.message, daysUntil: a.daysUntil || 0 });
    });

    users.forEach((u) => {
      const isSelf = u.uuid === currentUser.uuid;

      const bday = parseBirthday(u.birthday);
      if (bday) {
        const daysUntil = daysUntilAnnualDate(bday.month, bday.day, today);
        if (isSelf ? daysUntil === 0 : daysUntil <= 7) {
          items.push({
            type: 'birthday',
            name: u.name,
            daysUntil,
            dateLabel: formatAnnualDate(bday.month, bday.day),
            isSelf,
          });
        }
      }

      const hire = parseHireDate(u.date_of_hire);
      if (hire) {
        const daysUntil = daysUntilAnnualDate(hire.month, hire.day, today);
        // Years employed is measured against whichever year the NEXT
        // occurrence actually falls in, not the current calendar year — so
        // this stays correct across a Dec/Jan wraparound.
        const years = yearOfNextOccurrence(hire.month, hire.day, today) - hire.year;
        // A hire date exactly today with 0 years isn't an anniversary yet —
        // that's just day one, not something to announce here.
        if (years >= 1 && (isSelf ? daysUntil === 0 : daysUntil <= 7)) {
          items.push({
            type: 'anniversary',
            name: u.name,
            daysUntil,
            dateLabel: formatAnnualDate(hire.month, hire.day),
            years,
            isSelf,
          });
        }
      }
    });

    return items.sort((a, b) => a.daysUntil - b.daysUntil);
  }, [users, currentUser, announcements]);

  useEffect(() => {
    setIndex(0);
  }, [upcoming.length]);

  useEffect(() => {
    if (upcoming.length < 2) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % upcoming.length), ROTATE_MS);
    return () => clearInterval(id);
  }, [upcoming.length]);

  if (dismissed || upcoming.length === 0) return null;

  const current = upcoming[index];
  const Icon = current.type === 'birthday' ? IconCake : current.type === 'anniversary' ? IconAward : IconSpeakerphone;

  return (
    <div className="flex items-start justify-center gap-2 bg-gold text-white text-xs sm:text-sm px-3 pr-8 py-1.5 relative">
      <Icon size={14} stroke={2} className="flex-shrink-0 mt-px" />
      <span className="text-center">{buildMessage(current)}</span>
      <button
        onClick={() => {
          sessionDismissed = true;
          setDismissed(true);
        }}
        aria-label="Dismiss"
        className="absolute right-2 top-1.5 flex items-center justify-center w-5 h-5 rounded-full hover:bg-white/20 transition-colors flex-shrink-0"
      >
        <IconX size={13} stroke={2} />
      </button>
    </div>
  );
}

function buildMessage(item) {
  if (item.type === 'custom') {
    return item.message;
  }

  if (item.type === 'birthday') {
    if (item.isSelf) return `Happy Birthday, ${item.name.split(' ')[0]}! 🎉🎂`;
    if (item.daysUntil === 0) return `It's ${item.name}'s birthday today — ${item.dateLabel}! 🎉`;
    return `${item.daysUntil} day${item.daysUntil === 1 ? '' : 's'} 'til ${item.name}'s birthday on ${item.dateLabel}!`;
  }

  const yr = `${item.years} year${item.years === 1 ? '' : 's'}`;
  if (item.isSelf) return `Happy ${yr} work anniversary, ${item.name.split(' ')[0]}! 🎉🎊`;
  if (item.daysUntil === 0) return `🎉 Congrats to ${item.name} on ${yr} with the team today!`;
  return `${item.daysUntil} day${item.daysUntil === 1 ? '' : 's'} 'til ${item.name}'s ${yr} work anniversary on ${item.dateLabel}!`;
}
